import { oc } from "@orpc/contract";
import { z } from "zod";
import { PlanSourceSchema } from "../billing.js";
import { AccountRoleSchema } from "../identity.js";
import { PlanIdSchema } from "../plans.js";
import { SiteIdSchema } from "../sites.js";
// The admin dashboard (owner decisions 2026-10-10): a platform_admin's own test plan, the
// platform's aggregate counts from the tables that exist today, and the recent audit
// trail. Every procedure needs a session with the platform_admin role (the role column is
// reconciled from the allow-list at boot and on every login); the bootstrap's `admin`
// field is presentation only. Run and cost tiles arrive with Section 2.
const count = z.number().int().nonnegative();
/** Why the viewer cannot switch right now: Stripe is the truth for a paying account. */
export const PlanSwitchBlockerSchema = z.enum(["live_subscription", "open_change"]);
/**
 * The viewer's own plan as the switcher needs it: the EFFECTIVE plan and where it came
 * from (`planForAccount`), the raw stored test plan (null = none; stored but inactive
 * while a subscription is live), the blocker if any, and the site's plans to pick from.
 */
export const AdminPlanSwitchStateSchema = z.object({
    planId: PlanIdSchema,
    source: PlanSourceSchema,
    storedPlanId: PlanIdSchema.nullable(),
    blockedBy: PlanSwitchBlockerSchema.nullable(),
    options: z.array(z.object({ planId: PlanIdSchema, isDefault: z.boolean() })),
});
/** Aggregate counts for one site — never names, emails or per-account rows. */
const usd = z.number().nonnegative();
export const AdminSpendSchema = z.object({
    last24hUsd: usd,
    last30dUsd: usd,
    runs30d: count,
    byEngine30d: z.array(z.object({ engine: z.string(), usd, runs: count })),
    unavailable30d: count,
    /** Caveats on how the figures were priced (a proxy rate in the price book), rendered under them. */
    notes: z.array(z.string()),
});
export const AdminDashboardSiteSchema = z.object({
    siteId: SiteIdSchema,
    accounts: z.object({ active: count, suspended: count, closed: count, admins: count }),
    subscriptions: z.object({
        /** Live rows by raw provider status (active, past_due, …). */
        byStatus: z.record(z.string(), count),
        /** Live rows by the plan their entitled offer belongs to. */
        byPlan: z.array(z.object({ planId: PlanIdSchema, count })),
    }),
    openChanges: count,
    content: z.object({ publishedTemplates: count, libraries: count }),
    /**
     * What runs cost, in USD, from cost_records (provider-reported and estimated rows summed;
     * rows whose cost the provider never reported are counted, not summed). Aggregate only.
     */
    spend: AdminSpendSchema,
});
/** The webhook inbox is platform-wide (an event is stamped with its site only once resolved). */
export const AdminWebhookHealthSchema = z.object({
    pending: count,
    processed: count,
    quarantined: count,
    dead: count,
    oldestPendingAt: z.string().nullable(),
});
export const AdminAuditOutcomeSchema = z.enum(["success", "denied", "failure"]);
export const AdminAuditEventSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    siteId: SiteIdSchema,
    actorEmail: z.string().nullable(),
    actorRole: AccountRoleSchema,
    action: z.string(),
    outcome: AdminAuditOutcomeSchema,
    details: z.record(z.string(), z.unknown()),
});
export const AdminDashboardSchema = z.object({
    generatedAt: z.string(),
    planSwitch: AdminPlanSwitchStateSchema,
    sites: z.array(AdminDashboardSiteSchema),
    webhooks: AdminWebhookHealthSchema,
    /** The most recent admin actions, newest first — a limit, not a cursor. */
    recentAudit: z.array(AdminAuditEventSchema),
});
export const AdminPlanSwitchResultSchema = z.object({
    planId: PlanIdSchema,
    /** The previously STORED test plan (null = none); a retry answers changed: false. */
    previousPlanId: PlanIdSchema.nullable(),
    changed: z.boolean(),
});
export const admin = {
    overview: oc
        .route({
        summary: "The admin dashboard",
        description: "The viewer's own plan-switch state, aggregate counts per site (accounts, subscriptions, open changes, published content), the webhook inbox health and the most recent admin actions. Needs a session with the platform_admin role.",
    })
        .meta({ access: "read", keys: false })
        .output(AdminDashboardSchema),
    switchPlan: oc
        .route({
        summary: "Put my own account on a plan to test it",
        description: "Stores the plan on the signed-in admin's OWN account with no payment; the default plan stores nothing. Refused with 409 while the account has a live subscription (plan_switch_live_subscription) or an open change (plan_switch_pending_change) — Stripe stays the truth for a paying account. Audited; a repeat answers changed: false.",
    })
        .meta({ access: "write", keys: false })
        .input(z.object({ planId: PlanIdSchema }))
        .output(AdminPlanSwitchResultSchema),
};
//# sourceMappingURL=admin.js.map